import { isMatching, P } from 'ts-pattern'
import type { TaskSubmitFormValues } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import type {
  GenerationModelType,
  Task,
  TaskSummary,
} from 'src/services/apollo/generated/graphql'
import {
  TaskCancelability,
  TaskCharacteristics,
} from 'src/services/apollo/generated/graphql'
import { isDitV1Model, isSD3Model, isSDV1Model, isXLModel } from './model'

/**
 * Copied from packages/pixai-server/src/service/generationTask.ts
 */
export enum TaskStatus {
  waiting = 'waiting',
  running = 'running',
  completed = 'completed',
  cancelled = 'cancelled',
  failed = 'failed',
}

export function isTaskCancelable(
  task: Pick<Task, 'cancelability'> | null | undefined,
) {
  return task?.cancelability === TaskCancelability.Cancelable
}

/**
 * Check if a task is an animated image task
 *
 * AnimateDiff params
 * v2 params takes precedence; if v2 exists, v1 params should be ignored
 *
 * animateDiff: {
 *   enabled: boolean
 *   v2?: AnimateDiffV2Params
 * } & AnimateDiffV1Params
 *
 */
export function isAnimateDiffTask(task?: Pick<Task, 'parameters'>) {
  return Boolean(task?.parameters?.animateDiff?.enabled)
}

export function isVideoTask(task?: Pick<Task, 'parameters'>) {
  return (
    isAnimateDiffTask(task) ||
    isI2vProTask(task) ||
    isReferenceVideoTask(task) ||
    isT2I2VTask(task)
  )
}

export function isVideoTaskSummary(
  taskSummary?: Pick<TaskSummary, 'characteristics' | 'i2vProModel'>,
) {
  return (
    taskSummary?.characteristics?.includes(TaskCharacteristics.AnimatedDiff) ||
    taskSummary?.characteristics?.includes(TaskCharacteristics.I2VPro) ||
    taskSummary?.characteristics?.includes(TaskCharacteristics.T2I2V) ||
    Boolean(taskSummary?.i2vProModel)
  )
}

/**
 * Check is a task is a workflow task
 */
export const isWorkflowTask = isMatching({
  parameters: {
    workflowId: P.string,
  },
})

export const isI2vProTask = isMatching({
  parameters: {
    i2vPro: P.nonNullable,
  },
})

export const isReferenceVideoTask = isMatching({
  parameters: {
    referenceVideo: P.nonNullable,
  },
})

export const isT2I2VTask = isMatching({
  parameters: {
    t2i2v: P.nonNullable,
  },
})

export const isChatTask = isMatching({
  parameters: {
    chat: P.nonNullable,
  },
})

export const isArtFilterTask = isMatching({
  parameters: {
    model: 'pixai-image-filter',
  },
})

export function getTaskPublishedCount(task?: Task) {
  return task?.artworkIds?.length ?? 0
}

export const IPADAPATER_COMPAT_CONTROLNET_METHODS = [
  'dwpose',
  'openpose',
  'openpose_full',
]
export const SDXL_COMPAT_CONTROLNET_METHODS = [
  'canny',
  'depth',
  'openpose',
  'openpose_full',
]
/** Currently no cnets are compat with SD3 */
export const SD3_COMPAT_CONTROLNET_METHODS = [] as string[]
export const SD3_COMPAT_SAMPLING_METHODS = ['Euler', 'DDIM']

export function isCnetsCompatWithModelType(
  controlnets: Required<TaskSubmitFormValues>['controlNets'],
  modelType?: GenerationModelType,
) {
  if (!modelType) return false
  if (isDitV1Model(modelType)) return false
  if (isSDV1Model(modelType)) return true
  const compatList = isSD3Model(modelType)
    ? SD3_COMPAT_CONTROLNET_METHODS
    : isXLModel(modelType)
      ? SDXL_COMPAT_CONTROLNET_METHODS
      : undefined
  if (!compatList) return false
  return controlnets.every(cn => compatList.includes(cn.type))
}

export function isCnetsCompatWithIPAdapter(
  controlnets: Required<TaskSubmitFormValues>['controlNets'],
) {
  return controlnets.every(cn =>
    IPADAPATER_COMPAT_CONTROLNET_METHODS.includes(cn.type),
  )
}

export function getMediaIdsFromTask(
  task: Task,
  opts?: { stillImageOnly?: boolean },
): string[] {
  if (task.status !== TaskStatus.completed) return []
  if (opts?.stillImageOnly && isVideoTask(task)) return []
  if (task.outputs.batch) {
    return task.outputs.batch.map((v: { mediaId: string }) => v.mediaId)
  }
  const mediaId = getPrimaryMediaIdFromTask(task)
  return mediaId ? [mediaId] : []
}

export function getMediaIdsFromTasks(
  tasks: Task[],
  opts?: { stillImageOnly?: boolean },
): string[] {
  return tasks.flatMap(task => getMediaIdsFromTask(task, opts))
}

export function getMediaIdsFromTaskSummary(
  task: TaskSummary,
  opts?: { stillImageOnly?: boolean },
): string[] {
  if (task.status !== TaskStatus.completed) return []
  if (opts?.stillImageOnly && isVideoTaskSummary(task)) return []
  if (Array.isArray(task.batchMediaIds) && task.batchMediaIds.length > 0) {
    return task.batchMediaIds.filter((id): id is string => id !== undefined)
  }
  return task.mediaId ? [task.mediaId] : []
}

export function getMediaIdsFromTaskSummaries(
  tasks: TaskSummary[],
  opts?: { stillImageOnly?: boolean },
): string[] {
  return tasks.flatMap(task => getMediaIdsFromTaskSummary(task, opts))
}

export function taskHasFavImages(task?: Task): task is Task {
  if (!task) return false
  if (task.favoritedAt) return true
  if (((task.outputs.batch as Array<any>) ?? []).some(i => !!i.favoritedAt))
    return true
  return false
}

/**
 * For unsetting hires settings
 * @example setState(prev => ({ ...prev, ...emptyHiResParams }))
 */
export const emptyHiResParams = {
  upscale: undefined,
  upscaleDenoisingSteps: undefined,
  upscaleDenoisingStrength: undefined,
  upscaleSampler: undefined,
} as Partial<TaskSubmitFormValues>

export function isHiResEnabled(upscale: number | null | undefined) {
  return upscale != null && upscale > 1
}

export function getBaseMediaIdFromTask(
  task?: Pick<Task, 'parameters' | 'outputs'>,
) {
  if (isI2vProTask(task)) {
    return (task as Task)?.outputs?.baseMediaId as string | undefined
  }
  if (isReferenceVideoTask(task)) {
    const params = (task as Task)?.parameters?.referenceVideo
    return (
      params?.referenceImageMediaIds?.[0] ?? params?.referenceVideoMediaIds?.[0]
    )
  }

  return task?.parameters.mediaId
}

function getPrimaryMediaIdFromTaskOutputs(
  task?: Pick<Task, 'outputs'>,
): string | undefined {
  const outputs = task?.outputs as
    | {
        videos?: Array<{ mediaId?: string }>
        mediaIds?: Array<string | undefined>
        mediaId?: string
      }
    | undefined

  const videoMediaId = outputs?.videos?.find(
    v => typeof v?.mediaId === 'string' && v.mediaId.length > 0,
  )?.mediaId

  if (videoMediaId) return videoMediaId

  const mediaIdsOutput = outputs?.mediaIds?.find(
    (id): id is string => typeof id === 'string' && id.length > 0,
  )
  if (mediaIdsOutput) return mediaIdsOutput

  const mediaIdOutput =
    typeof outputs?.mediaId === 'string' && outputs.mediaId.length > 0
      ? outputs.mediaId
      : undefined
  if (mediaIdOutput) return mediaIdOutput

  return undefined
}

export function getPrimaryMediaIdFromTask(
  task?: Pick<Task, 'mediaId' | 'outputs'>,
): string | undefined {
  return task?.mediaId ?? getPrimaryMediaIdFromTaskOutputs(task)
}

/**
 * Returns the mediaId of the individual image at `index` inside a batch task,
 * falling back to `task.mediaId` when the index is absent, out of range, or
 * the resolved id is empty. Useful when a feature needs to key on the specific
 * image that will be published (e.g. tag recommendation cache) rather than
 * the composite preview represented by `task.mediaId`.
 */
export function getTaskMediaIdByIndex(
  task: Task,
  index: number | null | undefined,
): Task['mediaId'] {
  if (index != null) {
    const mediaId = getMediaIdsFromTask(task)[index]
    if (mediaId) return mediaId
  }
  return task.mediaId
}

/**
 * Convert model name so it can be used as a i18n key
 *
 * @example `v2.5` -> `v2-5`
 * @param modelName
 * @returns
 */
export function getI18nKeyForI2vModelName(modelName: string) {
  return modelName.replaceAll('.', '-')
}

/**
 * Whether it is allowed to create artwork from the specified task
 */
export function isTaskAllowedToPublishAsArtwork(task?: Task) {
  if (!task) {
    return false
  }

  return !task.parameters?.isPrivate
}

export function isTaskWithPromptHelper(task?: Task) {
  return Boolean(
    task?.parameters?.promptHelper?.enable ||
    task?.parameters?.promptHelper?.withStage,
  )
}
